// ProjectForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  useTheme,
} from "@mui/material";
import { Close, Save, Add, Delete } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import {
  PROJECT_STATUS,
  INSTALLATION_TYPES,
  INSTALLATION_TYPE_LABELS,
} from "../../utils/models/projectModel";

const ProjectForm = ({ project, onSave, onCancel }) => {
  const theme = useTheme();
  const { add: addProject, update: updateProject } =
    useAuditFirestore("projects");
  const { data: contracts } = useCollection("contracts");
  const { data: salesInvoices } = useCollection("salesInvoices");
  const { data: purchaseInvoices } = useCollection("purchaseInvoices");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: project?.name || "",
    description: project?.description || "",
    contractId: project?.contractId || "",
    contractTitle: project?.contractTitle || "",
    clientId: project?.clientId || "",
    clientName: project?.clientName || "",
    clientPhone: project?.clientPhone || "",
    address: {
      street: project?.address?.street || "",
      city: project?.address?.city || "",
      province: project?.address?.province || "",
      zipCode: project?.address?.zipCode || "",
      country: project?.address?.country || "España",
    },
    installationType:
      project?.installationType || INSTALLATION_TYPES.PHOTOVOLTAIC,
    status: project?.status || PROJECT_STATUS.MANAGING,
    budget: project?.budget || 0,
    totalInvoiced: project?.totalInvoiced || 0,
    startDate: project?.startDate || "",
    endDate: project?.endDate || "",
    installationDate: project?.installationDate || "",
    process: project?.process || "",
    hasFileOpened: project?.hasFileOpened || false,
    hasLicensePermit: project?.hasLicensePermit || false,
    materialPurchased: project?.materialPurchased || false,
    laborHired: project?.laborHired || false,
    materialReceived: project?.materialReceived || false,
    liftingEquipmentRented: project?.liftingEquipmentRented || false,
    installed: project?.installed || false,
    hasBulletinRequested: project?.hasBulletinRequested || false,
    hasBulletinReceived: project?.hasBulletinReceived || false,
    hasRequirement: project?.hasRequirement || false,
    notes: project?.notes || "",
    bulletinLink: project?.bulletinLink || "",
    bulletinFileName: project?.bulletinFileName || "",
    requirementLink: project?.requirementLink || "",
    requirementFileName: project?.requirementFileName || "",
    requirementReminderDate: project?.requirementReminderDate || "",
    files: project?.files || [],
  });

  useEffect(() => {
    if (formData.status === PROJECT_STATUS.COMPLETED && project?.id) {
      const allInvoices = [
        ...(salesInvoices?.filter(
          (inv) =>
            inv.isActive !== false &&
            inv.projects?.some((p) => p.projectId === project.id),
        ) || []),
        ...(purchaseInvoices?.filter(
          (inv) =>
            inv.isActive !== false &&
            inv.projects?.some((p) => p.projectId === project.id),
        ) || []),
      ];
      const total = allInvoices.reduce((sum, inv) => {
        const pa = inv.projects?.find((p) => p.projectId === project.id);
        return sum + (pa?.amount || inv.total || 0);
      }, 0);
      setFormData((prev) => ({ ...prev, totalInvoiced: total }));
    }
  }, [formData.status, project?.id, salesInvoices, purchaseInvoices]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nameParts = name.split(".");
    if (nameParts.length === 2) {
      setFormData((prev) => ({
        ...prev,
        [nameParts[0]]: { ...prev[nameParts[0]], [nameParts[1]]: value },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    if (error) setError("");
  };

  // Manejar archivos
  const handleFileChange = (index, field, value) => {
    const newFiles = [...formData.files];
    newFiles[index] = { ...newFiles[index], [field]: value };
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleAddFile = () => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, { name: "", url: "" }],
    }));
  };

  const handleRemoveFile = (index) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleContractSelect = (contractId) => {
    const contract = contracts?.find((c) => c.id === contractId);
    if (contract) {
      setFormData((prev) => ({
        ...prev,
        contractId,
        contractTitle: contract.title,
        clientId: contract.clientId,
        clientName: contract.clientName,
        clientPhone: contract.clientPhone || "",
        address: {
          street: contract.address?.street || "",
          city: contract.address?.city || "",
          province: contract.address?.province || "",
          zipCode: contract.address?.zipCode || "",
          country: contract.address?.country || "España",
        },
        name: contract.title,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      setError("El nombre es obligatorio");
      return;
    }

    // Filtrar archivos vacíos
    const validFiles = formData.files.filter(
      (file) => file.name.trim() !== "" && file.url.trim() !== "",
    );

    setLoading(true);
    setError("");
    try {
      const projectData = {
        ...formData,
        files: validFiles,
        updatedAt: new Date().toISOString(),
      };
      if (project?.id) {
        await updateProject(project.id, projectData);
      } else {
        await addProject({
          ...projectData,
          team: [],
          createdAt: new Date().toISOString(),
          createdBy: "",
          isActive: true,
        });
      }
      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };
  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}
        >
          {project?.id ? "Editar proyecto" : "Nuevo proyecto"}
        </Typography>
        <IconButton
          onClick={onCancel}
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </Box>
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: { xs: 2, sm: 3 },
            borderRadius: "12px",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Typography sx={sectionTitleSx}>Contrato relacionado</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
            Seleccionar contrato
          </InputLabel>
          <Select
            name="contractId"
            value={formData.contractId}
            onChange={(e) => handleContractSelect(e.target.value)}
            label="Seleccionar contrato"
            sx={{
              borderRadius: "12px",
              background: theme.palette.background.paper,
              height: { xs: 40, sm: 44 },
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {contracts
              ?.filter((c) => c.isActive !== false)
              .map((c) => (
                <MenuItem
                  key={c.id}
                  value={c.id}
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  {c.title}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <Typography sx={sectionTitleSx}>Datos del proyecto</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2.5 },
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Nombre"
            name="name"
            value={formData.name}
            onChange={handleChange}
            sx={fieldSx}
            required
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Descripción"
            name="description"
            value={formData.description}
            onChange={handleChange}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                background: theme.palette.background.paper,
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Cliente"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "260px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Teléfono"
              name="clientPhone"
              value={formData.clientPhone}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "220px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>
        </Box>

        <Typography sx={sectionTitleSx}>Dirección de instalación</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2.5 },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Calle y número"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Población/Ciudad"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Provincia"
              name="address.province"
              value={formData.address.province}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Código Postal"
              name="address.zipCode"
              value={formData.address.zipCode}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="País"
              name="address.country"
              value={formData.address.country}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>
        </Box>

        <Typography sx={sectionTitleSx}>Tipo y estado</Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, sm: 2.5 },
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <FormControl
            fullWidth
            size="small"
            sx={{ flex: 1, minWidth: { xs: "100%", sm: "260px" } }}
          >
            <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
              Tipo de instalación
            </InputLabel>
            <Select
              name="installationType"
              value={formData.installationType}
              onChange={handleChange}
              label="Tipo de instalación"
              sx={{
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: { xs: 40, sm: 44 },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {Object.entries(INSTALLATION_TYPE_LABELS).map(
                ([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    {label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          <FormControl
            fullWidth
            size="small"
            sx={{ flex: 1, minWidth: { xs: "100%", sm: "260px" } }}
          >
            <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
              Estado
            </InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              label="Estado"
              sx={{
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: { xs: 40, sm: 44 },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              <MenuItem
                value="managing"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
              >
                Gestionando
              </MenuItem>
              <MenuItem
                value="installing"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
              >
                Instalando
              </MenuItem>
              <MenuItem
                value="completed"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
              >
                Finalizado
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {formData.status === PROJECT_STATUS.COMPLETED && (
          <>
            <Typography sx={sectionTitleSx}>Total facturado</Typography>
            <TextField
              fullWidth
              size="small"
              label="Total acumulado de facturas"
              name="totalInvoiced"
              type="number"
              value={formData.totalInvoiced}
              onChange={handleChange}
              sx={{ ...fieldSx, maxWidth: { xs: "100%", sm: "400px" } }}
              InputProps={{ endAdornment: "€" }}
              helperText="Suma de todas las facturas asociadas a este proyecto"
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </>
        )}

        <Typography sx={sectionTitleSx}>Fechas</Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, sm: 2.5 },
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Fecha inicio"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "220px" } }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Fecha fin"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "220px" } }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Fecha instalación"
            name="installationDate"
            value={formData.installationDate}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "220px" } }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
        </Box>

        <Typography sx={sectionTitleSx}>Checklist de proceso</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 1,
            width: "100%",
          }}
        >
          {[
            { name: "hasFileOpened", label: "Apertura expediente" },
            { name: "hasLicensePermit", label: "Licencia de obra" },
            { name: "materialPurchased", label: "Material comprado" },
            { name: "laborHired", label: "Mano de obra contratada" },
            { name: "materialReceived", label: "Material recibido" },
            {
              name: "liftingEquipmentRented",
              label: "Equipo de elevación alquilado",
            },
            { name: "installed", label: "Instalación realizada" },
            { name: "hasBulletinRequested", label: "Boletín solicitado" },
            { name: "hasBulletinReceived", label: "Boletín recibido" },
            { name: "hasRequirement", label: "Requerimiento" },
          ].map((item) => (
            <FormControlLabel
              key={item.name}
              control={
                <Checkbox
                  name={item.name}
                  checked={formData[item.name]}
                  onChange={handleChange}
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                  {item.label}
                </Typography>
              }
            />
          ))}
        </Box>

        <Typography sx={sectionTitleSx}>Documentos</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 2,
                color: theme.palette.primary.main,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              📋 Boletín / Proyecto
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1.5 },
                alignItems: "center",
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                size="small"
                label="Nombre del archivo"
                name="bulletinFileName"
                value={formData.bulletinFileName}
                onChange={handleChange}
                placeholder="boletin.pdf"
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                size="small"
                label="Enlace (URL)"
                name="bulletinLink"
                value={formData.bulletinLink}
                onChange={handleChange}
                placeholder="https://drive.google.com/..."
                sx={{
                  ...fieldSx,
                  flex: 2,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 2,
                color: theme.palette.warning.main,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              ⚠️ Requerimiento
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Fecha recordatorio requerimiento"
              name="requirementReminderDate"
              value={formData.requirementReminderDate}
              onChange={handleChange}
              sx={{ ...fieldSx, mb: 2 }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1.5 },
                alignItems: "center",
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                size="small"
                label="Nombre del archivo"
                name="requirementFileName"
                value={formData.requirementFileName}
                onChange={handleChange}
                placeholder="requerimiento.pdf"
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                size="small"
                label="Enlace (URL)"
                name="requirementLink"
                value={formData.requirementLink}
                onChange={handleChange}
                placeholder="https://drive.google.com/..."
                sx={{
                  ...fieldSx,
                  flex: 2,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* ARCHIVOS */}
        <Typography sx={sectionTitleSx}>Archivos</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
          }}
        >
          {formData.files.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1.5 },
                alignItems: "center",
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                size="small"
                label="Nombre del archivo"
                value={file.name}
                onChange={(e) =>
                  handleFileChange(index, "name", e.target.value)
                }
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                size="small"
                label="Enlace (URL)"
                value={file.url}
                onChange={(e) => handleFileChange(index, "url", e.target.value)}
                sx={{
                  ...fieldSx,
                  flex: 2,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <IconButton
                onClick={() => handleRemoveFile(index)}
                size="small"
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: theme.palette.error.main,
                    color: "white",
                  },
                }}
              >
                <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddFile}
            sx={{
              borderRadius: "12px",
              height: { xs: 40, sm: 44 },
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              alignSelf: "flex-start",
              mt: 1,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              "&:hover": {
                borderColor: theme.palette.primary.dark,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(0, 175, 163, 0.1)"
                    : "rgba(0, 95, 101, 0.05)",
              },
            }}
          >
            Añadir archivo
          </Button>
        </Box>

        <Typography sx={sectionTitleSx}>Presupuesto</Typography>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Presupuesto (€)"
          name="budget"
          value={formData.budget}
          onChange={handleChange}
          sx={fieldSx}
          inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
        />

        <Typography sx={sectionTitleSx}>Proceso</Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Proceso"
          name="process"
          value={formData.process}
          onChange={handleChange}
          inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              background: theme.palette.background.paper,
            },
          }}
        />

        <Typography sx={sectionTitleSx}>Notas internas</Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Notas"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              background: theme.palette.background.paper,
            },
          }}
        />

        <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              borderRadius: "12px",
              px: { xs: 2, sm: 3 },
              height: { xs: 40, sm: 44 },
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
            fullWidth={{ xs: true, sm: false }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<Save />}
            sx={{
              borderRadius: "12px",
              px: { xs: 2, sm: 3 },
              height: { xs: 40, sm: 44 },
              boxShadow: "none",
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
            fullWidth={{ xs: true, sm: false }}
          >
            {loading
              ? "Guardando..."
              : project?.id
                ? "Actualizar"
                : "Crear proyecto"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ProjectForm;
